import { useState, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useShopifySearch } from 'lib/hooks/useShopifySearch'
import { flattenConnection } from '@shopify/hydrogen-react'
import { useRouter } from 'next/router'

import { CloseButton } from 'components/Icons/CloseButton'
import { SearchResult } from './SearchResult'
import { ViewAllSearchLink } from './ViewAllSearchLink'
import { SearchIcon } from '../SearchIcon'
import { Button } from '../Button'

export const MobileSearchButton = ({ closeMenu }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const router = useRouter()
  const { data, loading, error, query, reset, onChange } = useShopifySearch({
    limit: 3,
  })

  const resetSearch = () => {
    closeMenu()
    setOpen(false)
    reset()
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.length > 2) {
      router.push(`/search?q=${query}`, undefined, { scroll: false })
      resetSearch()
    }
  }

  return (
    <div>
      <li>
        <Button className="bg-white" onClick={() => setOpen(true)}>
          Search
        </Button>
      </li>
      <AnimatePresence mode="wait">
        {open ? (
          <motion.div
            className="h-screen-dynamic fixed inset-0 z-[10] flex w-full flex-col justify-between overflow-hidden bg-[#E6E6E6] p-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'linear',
            }}
          >
            <div className="fixed left-0 top-0 flex w-full justify-between p-20">
              <Button
                onClick={() => setOpen(false)}
                className="bg-white px-16 py-10"
              >
                Back
              </Button>
              <Button onClick={closeMenu} className="bg-white px-14 py-12">
                <CloseButton className="h-10 w-10" />
              </Button>
            </div>
            <div className="flex flex-1 flex-col pt-100">
              <form onSubmit={handleSubmit} className="relative w-full">
                <input
                  ref={inputRef}
                  type="text"
                  onChange={onChange}
                  placeholder="What are you looking for?"
                  className="w-full rounded-8 bg-white px-16 py-14 text-16 text-darkGray placeholder-darkGray placeholder-opacity-50 focus:outline-none"
                />
                <button
                  type="submit"
                  className="absolute right-16 top-1/2 -translate-y-1/2"
                >
                  <SearchIcon />
                </button>
              </form>
              <AnimatePresence>
                {open && query.length > 2 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 2 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                      opacity: 0,
                      y: -2,
                      transition: {
                        duration: 0.4,
                        ease: [0.9, 0.01, 0.11, 0.98],
                        delay: 0,
                      },
                    }}
                    transition={{
                      duration: 0.6,
                      delay: 0.4,
                      ease: [0.9, 0.01, 0.11, 0.98],
                    }}
                    className="relative mt-14 flex h-full w-full flex-1 flex-col justify-between gap-8"
                  >
                    <div className="flex flex-col gap-8">
                      {loading && data.length < 1 && (
                        <div>Searching for "{query}"</div>
                      )}
                      {!loading && data.length < 1 && (
                        <div>No Results for "{query}"</div>
                      )}
                      {data.length >= 1 &&
                        data?.map((product: any) => {
                          const images: any = flattenConnection(product.images)
                          const src = images?.[0]?.src
                          return (
                            <SearchResult
                              key={product._id}
                              {...product}
                              images={images}
                              src={src}
                              onClick={resetSearch}
                            />
                          )
                        })}
                    </div>
                    {data.length >= 1 ? (
                      <ViewAllSearchLink onClick={resetSearch} query={query} />
                    ) : null}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}
